<template>
  <div>
    <div>
      <div
        class="d-flex align-items-center flex-wrap"
      >
        <GroupOrdersDropdown
          class="mr-3 mb-1"
          :group-key.sync="groupKey"
          :subscriptions="true"
        />
        <PayersFilterDropdown
          class="mr-3 mb-1"
          @selected="selectedPayers = $event"
        />
        <div
          v-if="!pendingDownload"
          v-tippy
          class="secondary-icon ml-3"
          :content="$t('shop.downloadReport')"
          @click="download"
        >
          <i class="fas fa-download" />
        </div>
        <div v-else>
          <Loader />
        </div>

        <div
          class="flex-grow-1 d-flex align-items-center justify-content-end"
        >
          <div>
            <button
              class="btn btn-sm btn-primary"
              data-test="toggle-add-client"
              @click="showOrderForm"
            >
              <i class="fas fa-plus pr-1" />
              {{ $t('shop.addSubscriptions') }}
            </button>
          </div>
        </div>
      </div>
      <hr class="my-2">
      <div class="d-flex mb-1">
        <ReportCheckbox
          v-model="showReserveQueue"
          class="mr-3"
          :text="$t('shop.showReserveQueue')"
        />
        <!-- <MonthDropdown
          :title="$t('shop.orderedDate')"
          class="mr-3"
          small
          :selected.sync="ordered"
        /> -->
        <LayoutConfig
          :layout="showReserveQueue ? reserveLayout : layout"
          class="mr-3"
          :other-columns="extraColumns"
          :save="saveLayout"
        />
      </div>
    </div>

    <ColumnEdit
      :edit="!!edited || editedItems.length > 0"
      :focused="false"
      class="h-100"
    >
      <SubscriptionList
        v-if="payers"
        ref="list"
        class="flex-grow-1"
        :layout="showReserveQueue ? reserveLayout : layout"
        :reserve-queue="showReserveQueue"
        :aggregated-subscriptions="aggregatedSubscriptions"
        :pending="pending"
        :group-by="groupKey"
        :product-sets="listings"
        :filter="filter"
        :payers="payers"
        :edited-id="edited ? edited.id : null"
        @refresh="request(true)"
        @select="selectOrder"
        @bulkEdit="editedItems = $event; edited = null"
      />

      <template #edit>
        <div
          v-if="edited || editedItems.length > 0"
          class="page-cart order-details"
        >
          <SingleOrderDetails
            v-if="edited"
            :key="edited.id"
            :subscription="true"
            :order="edited"
            @close="edited = null; editedItems = []"
            @refresh="request(true)"
          />
        </div>
      </template>
    </ColumnEdit>
  </div>
</template>

<script>
import ColumnEdit from '@/components/layout/ColumnEdit';
import PayersFilterDropdown from '@/components/payers/PayersFilterDropdown';
import ReportCheckbox from '@/components/reports/ReportCheckbox';
import GroupOrdersDropdown from '@/components/shop/report/GroupOrdersDropdown';
import SubscriptionList from '@/components/shop/report/subscriptions/SubscriptionList';
import LayoutConfig from '@/components/shop/report/table/LayoutConfig';
import getFileName from '@/utils/file-name';
import { mapActions, mapGetters } from 'vuex';
import { extraColumns } from './order/columns';
import SingleOrderDetails from './order/SingleOrderDetails';

const groupByStorageKey = 'shop_groupBy:subscriptions';

export default {
  data: () => ({
    focus: false,
    subscriptionGroups: null,
    groupKey: 'payers',
    selectedPayers: [],
    showReserveQueue: false,
    payers: null,
    pending: false,
    pendingDownload: false,
    haveLayout: false,
    layout: [],
    edited: null,
    editedItems: [],
    requestId: 0,
    listings: {},
  }),
  components: {
    SubscriptionList,
    ColumnEdit,
    LayoutConfig,
    SingleOrderDetails,
    GroupOrdersDropdown,
    PayersFilterDropdown,
    ReportCheckbox,
  },
  computed: {
    ...mapGetters(['payeeId', 'payee']),
    ...mapGetters('shop', [
      'catalogDetails',
      'singleOrderSchema',
    ]),
    extraColumns() {
      return extraColumns
        .filter((x) => x.key !== 'month' || this.catalogDetails.orderSchema === 'Monthly')
        .filter((x) => x.key !== 'day' || this.catalogDetails.orderSchema === 'Daily');
    },
    reserveLayout() {
      return this.layout
        .filter((x) => x.key !== 'month' && x.key !== 'day' && x.key !== 'position')
        .concat([{
          key: 'position',
          type: 'position',
          name: 'Pozycja w kolejce',
          icon: 'far fa-arrow-up-short-wide',
          width: 140,
        }]);
    },
    availableDays() {
      return Object.keys(this.dayStatus);
    },
    payersDictionary() {
      return Object.freeze(this.payers.reduce((acc, curr) => {
        acc[curr.id] = curr;
        return acc;
      }, {}));
    },
    aggregatedSubscriptions() {
      if (this.subscriptionGroups == null) return [];

      if (this.groupKey === 'payers') {
        return this.subscriptionGroups.map((o) => ({
          ...o,
          id: o.group,
          totalPrice: o.totalValue,
          currency: 'pln',
          payerId: o.group,
          payer: this.payersDictionary[o.group],
        }))
          .sort((a, b) => (a.payer?.name || '').localeCompare(b.payer?.name));
      }
      if (this.groupKey === 'productSets') {
        return this.subscriptionGroups.map((o) => ({
          ...o,
          id: o.group,
          listingName: o.groupName,
          totalPrice: o.totalValue,
          currency: 'pln',
          productSetId: o.group,
        }))
          .sort((a, b) => (a.listingName || '').localeCompare(b.listingName || ''));
      }

      return [];
    },
    filter() {
      return {
        payerIds: this.selectedPayers.map((x) => x.id),
        groupBy: this.groupKey,
        showReserveQueue: this.showReserveQueue,
      };
    },
  },
  watch: {
    filter() {
      this.pending = true;
      this.subscriptionGroups = null;
      this.debounceRefreshAggregatedSubscriptions();
    },
    groupKey(v) {
      localStorage.setItem(groupByStorageKey, v);
    },
  },
  methods: {
    ...mapActions(['getPayers']),
    ...mapActions('shop', [
      'getAggregatedSubscriptions',
      'getSubscriptionsReport',
      'setCatalogSubscriptionsLayout',
      'getCatalogListings',
    ]),
    download() {
      this.pendingDownload = true;

      this.getSubscriptionsReport({
        data: {
          catalogId: this.catalogDetails.id,
          payerIds: this.selectedPayers.map((x) => x.id),
          properties: (this.showReserveQueue ? this.reserveLayout : this.layout).map((x) => x.key),
          reserveQueue: this.showReserveQueue,
        },
      })
        .then(({ data, headers }) => {
          const url = window.URL.createObjectURL(new Blob([data]));
          const link = document.createElement('a');
          link.href = url;

          link.setAttribute('download', getFileName(headers));
          document.body.appendChild(link);
          link.click();
        })
        .finally(() => {
          this.pendingDownload = false;
        });
    },
    saveLayout(layout) {
      return this.setCatalogSubscriptionsLayout({
        params: {
          catalogId: this.catalogDetails.id,
        },
        data: {
          layout: JSON.stringify(layout),
        },
      })
        .then(() => {
          this.layout = layout;
        });
    },
    showOrderForm() {
      this.$router.push(`/payee/${this.payeeId}/shop/${this.catalogDetails.id}/subscription`);
    },
    selectOrder(o) {
      if (this.edited && this.edited.id === o.id) {
        this.edited = null;
      } else {
        this.edited = o;
        this.editedItems = [];
      }
    },
    initLayout() {
      this.layout = [
        { key: 'name', type: 'default' },
        { key: 'code', type: 'default' },
        { key: 'productSetName', type: 'default' },
        { key: 'space', type: 'default' },
        { key: 'created', type: 'default' },
        { key: 'price', type: 'default' },
      ];

      try {
        if (this.catalogDetails.subscriptionsLayout) {
          this.layout = JSON.parse(this.catalogDetails.subscriptionsLayout);
        }
      } catch {
        //
      }

      this.haveLayout = true;
      this.requestPayers();
    },
    refreshLayout() {
      this.initLayout();
      this.refresh();
    },
    request(refresh) {
      if (this.groupKey === 'none') {
        this.$refs.list.request('none', true);
      }

      if (!refresh) {
        this.pending = true;
        this.subscriptionGroups = [];
      }

      this.debounceRefreshAggregatedSubscriptions();
    },
    debounceRefreshAggregatedSubscriptions() {
      if (this.debounceRequest) {
        clearTimeout(this.debounceRequest);
      }

      this.debounceRequest = setTimeout(() => {
        this.refreshAggregatedSubscriptions();
      }, 500);
    },
    refreshAggregatedSubscriptions() {
      if (this.groupKey === 'none') {
        this.pending = false;
        return;
      }

      this.requestId += 1;
      this.getAggregatedSubscriptions({
        params: {
          query: {
            catalogId: this.catalogDetails.id,
            reserveQueue: this.showReserveQueue,
          },
          requestId: this.requestId,
        },
        data: {
          payerIds: this.selectedPayers.map((x) => x.id),
          groupBy: this.groupKey,
        },
      })
        .then(({ data, config }) => {
          if (config.params.requestId === this.requestId) {
            this.subscriptionGroups = data;
          }
        })
        .finally(() => {
          this.pending = false;
        });
    },
    requestPayers() {
      if (!this.haveLayout) return;

      const withProperty = this.layout.filter((x) => x.type === 'property').map((x) => x.key);
      const withGroup = this.layout.filter((x) => x.type === 'group').map((x) => x.key);

      this.getPayers({
        params: {
          query: {
            skip: 0,
            take: -1,
            withProperty,
            withGroup,
          },
        },
      })
        .then(({ data }) => {
          this.payers = Object.freeze(data);
        });
    },
    requestListings() {
      this.getCatalogListings({
        params: {
          catalogId: this.catalogDetails.id,
        },
      })
        .then(({ data }) => {
          data.forEach((ps) => {
            this.$set(this.listings, ps.id, ps);
          });
        });
    },
  },
  created() {
    this.groupKey = localStorage.getItem(groupByStorageKey) || 'payers';

    this.pending = true;
    this.initLayout();
    this.requestPayers();
    this.requestListings();
    this.debounceRefreshAggregatedSubscriptions();

    this.$emit('page', 'subscriptions');
  },
};
</script>

<style lang="scss">
  .smaller-icon {
    font-size: 0.85rem;
  }

  .order-details {
    height: calc(100vh - 310px);
    overflow: auto;
  }
</style>
